/**
 * Tiny line-based tokenizer for marketing code blocks.
 * Same token colors as the original prelaunch: kw / str / dim / ok / prompt.
 */
import "./hl.css";

export interface Tok {
  cls?: string;
  s: string;
}

const TOKEN =
  /(\/\/[^\n]*|\{\{--[\s\S]*?--\}\}|<!--[\s\S]*?-->)|('[^'\n]*'|"[^"\n]*"|`[^`\n]*`)|(--[a-z][\w-]*|\b(?:import|from|export|default|function|const|let|return|await|new|npx|script|div)\b)/g;

function tokenizeLine(line: string, out: Tok[]) {
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(line)) !== null) {
    if (m.index > last) out.push({ s: line.slice(last, m.index) });
    out.push({ cls: m[1] ? "tok-d" : m[2] ? "tok-s" : "tok-k", s: m[0] });
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push({ s: line.slice(last) });
}

export function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  const lines = src.replace(/^\n/, "").replace(/\n$/, "").split("\n");
  lines.forEach((line, i) => {
    const nl = i < lines.length - 1 ? "\n" : "";
    if (line.startsWith("$ ")) {
      out.push({ cls: "tok-p", s: "$ " });
      tokenizeLine(line.slice(2), out);
      if (nl) out.push({ s: nl });
    } else if (/^\s*✓/.test(line)) {
      out.push({ cls: "tok-ok", s: line + nl });
    } else if (/^\s*(→|#|\{\{--|<!--|---$)/.test(line)) {
      out.push({ cls: "tok-d", s: line + nl });
    } else {
      tokenizeLine(line, out);
      if (nl) out.push({ s: nl });
    }
  });
  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Highlighted HTML string — avoids keyed-list for moon-sized marketing bundles. */
export function tokenizeHtml(src: string): string {
  let html = "";
  for (const t of tokenize(src)) {
    const body = escapeHtml(t.s);
    html += t.cls ? `<span class="${t.cls}">${body}</span>` : body;
  }
  return html;
}
